import { BufferAttribute, Color, DynamicDrawUsage, Group, InstancedBufferAttribute, InstancedBufferGeometry, Mesh, Vector3 } from 'three';
import type { Object3D, Texture } from 'three';
import { createAmbientMaterial, createBurstMaterial } from '@cloudpearl/assets';
import type { SharedUniforms } from '@cloudpearl/assets';
import type { MapDef } from '@cloudpearl/config';

export const P = { Puff: 0, Spark: 1, Petal: 2, Grain: 3, Streak: 4, Dust: 5 } as const;
export type P = (typeof P)[keyof typeof P];

function quad(): InstancedBufferGeometry {
  const g = new InstancedBufferGeometry();
  g.setAttribute('position', new BufferAttribute(new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
  g.setIndex([0, 1, 2, 0, 2, 3]);
  return g;
}

class BurstBuffer {
  readonly mesh: Mesh;
  private readonly p0: InstancedBufferAttribute;
  private readonly v0: InstancedBufferAttribute;
  private readonly params: InstancedBufferAttribute;
  private readonly color: InstancedBufferAttribute;
  private head = 0;
  private lo = Infinity;
  private hi = -1;
  readonly capacity: number;

  constructor(capacity: number, shared: SharedUniforms, additive: boolean, heightfield: Texture) {
    this.capacity = capacity;
    const g = quad();
    const mk = (): InstancedBufferAttribute => {
      const a = new InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
      a.setUsage(DynamicDrawUsage);
      return a;
    };
    this.p0 = mk();
    this.v0 = mk();
    this.params = mk();
    this.color = mk();
    // Start everything dead.
    for (let i = 0; i < capacity; i++) (this.p0.array as Float32Array)[i * 4 + 3] = -1e6;
    g.setAttribute('aP0', this.p0);
    g.setAttribute('aV0', this.v0);
    g.setAttribute('aParams', this.params);
    g.setAttribute('aColor', this.color);
    g.instanceCount = capacity;
    this.mesh = new Mesh(g, createBurstMaterial(shared, additive, heightfield));
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = additive ? 12 : 10;
  }

  spawn(time: number, pos: Vector3, vel: Vector3, life: number, size0: number, size1: number, type: number, color: Color, alpha: number): void {
    const i = this.head;
    this.head = (this.head + 1) % this.capacity;
    const o = i * 4;
    put(this.p0.array as Float32Array, o, pos.x, pos.y, pos.z, time);
    put(this.v0.array as Float32Array, o, vel.x, vel.y, vel.z, life);
    put(this.params.array as Float32Array, o, size0, size1, type, Math.random());
    put(this.color.array as Float32Array, o, color.r, color.g, color.b, alpha);
    this.lo = Math.min(this.lo, i);
    this.hi = Math.max(this.hi, i);
  }

  flush(): void {
    if (this.hi < 0) return;
    for (const a of [this.p0, this.v0, this.params, this.color]) {
      a.clearUpdateRanges();
      a.addUpdateRange(this.lo * 4, (this.hi - this.lo + 1) * 4);
      a.needsUpdate = true;
    }
    this.lo = Infinity;
    this.hi = -1;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as { dispose(): void }).dispose();
  }
}

/** One-shot effects (dust, sparkles, petals, sand spray, crash poofs). */
function put(a: Float32Array, o: number, x: number, y: number, z: number, w: number): void {
  a[o] = x;
  a[o + 1] = y;
  a[o + 2] = z;
  a[o + 3] = w;
}

export class Bursts {
  /** Both burst meshes (hidden as one, e.g. for reflection probes). */
  readonly group = new Group();
  private readonly normal: BurstBuffer;
  private readonly additive: BurstBuffer;
  private readonly v = new Vector3();
  private readonly c = new Color();

  constructor(parent: Object3D, shared: SharedUniforms, heightfield: Texture, capacity = 1536) {
    this.normal = new BurstBuffer(capacity, shared, false, heightfield);
    this.additive = new BurstBuffer(Math.floor(capacity / 2), shared, true, heightfield);
    this.group.add(this.normal.mesh, this.additive.mesh);
    parent.add(this.group);
  }

  emit(time: number, type: P, pos: Vector3, vel: Vector3, life: number, size0: number, size1: number, color: Color, alpha = 1): void {
    const buf = type === P.Spark || type === P.Streak ? this.additive : this.normal;
    buf.spawn(time, pos, vel, life, size0, size1, type, color, alpha);
  }

  /** Spray `n` particles in a cone around `dir` with speed range. */
  spray(time: number, n: number, type: P, pos: Vector3, dir: Vector3, spread: number, speed: [number, number], life: [number, number], size: [number, number, number], color: Color, colorJitter = 0.08, alpha = 1): void {
    for (let i = 0; i < n; i++) {
      const s = speed[0] + Math.random() * (speed[1] - speed[0]);
      this.v.set(dir.x + (Math.random() - 0.5) * spread, dir.y + (Math.random() - 0.5) * spread, dir.z + (Math.random() - 0.5) * spread).normalize().multiplyScalar(s);
      const k = 1 + (Math.random() - 0.5) * colorJitter * 2;
      this.c.setRGB(color.r * k, color.g * k, color.b * k);
      const l = life[0] + Math.random() * (life[1] - life[0]);
      const sz = size[0] + Math.random() * (size[1] - size[0]);
      this.emit(time, type, pos, this.v, l, sz, sz * size[2], this.c, alpha);
    }
  }

  flush(): void {
    this.normal.flush();
    this.additive.flush();
  }

  dispose(): void {
    this.group.removeFromParent();
    this.normal.dispose();
    this.additive.dispose();
  }
}

/** Drifting pollen (Sky Isles) or blowing, glinting sand (Dunes) around the camera. */
export class AmbientParticles {
  readonly mesh: Mesh;
  private readonly center: Vector3;

  constructor(parent: Object3D, shared: SharedUniforms, map: MapDef, count: number, heightfield: Texture) {
    const g = quad();
    const seeds = new Float32Array(count * 4);
    for (let i = 0; i < seeds.length; i++) seeds[i] = Math.random();
    g.setAttribute('aSeed', new InstancedBufferAttribute(seeds, 4));
    g.instanceCount = count;
    const kind = map.wind.particles;
    const col = new Color(kind === 'sand' ? '#fff1d8' : '#fff7d6');
    const glint = new Color(kind === 'sand' ? map.sand.sparkle : '#ffffff');
    const mat = createAmbientMaterial(shared, kind, new Vector3(col.r, col.g, col.b).multiplyScalar(kind === 'sand' ? 0.9 : 1.4), new Vector3(glint.r, glint.g, glint.b), heightfield);
    this.mesh = new Mesh(g, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 11;
    this.center = mat.uniforms['uBoxCenter']!.value as Vector3;
    parent.add(this.mesh);
  }

  follow(camPos: Vector3, forward: Vector3): void {
    this.center.copy(camPos).addScaledVector(forward, 34);
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    (this.mesh.material as { dispose(): void }).dispose();
  }
}
